/**
 * Reading the generated OpenAPI document, and turning it into things a
 * developer can use.
 *
 * ONE SOURCE, THREE OUTPUTS. The reference page, the API console and the
 * Postman export all read the same `openapi.json` the gate serves — which is
 * itself generated from the catalog that routes the requests. Nothing here
 * maintains a second list of endpoints, so the reference cannot describe an
 * endpoint that does not exist and the console cannot call one the docs do not
 * mention. That is the whole reason the document is generated rather than
 * written.
 *
 * `$ref` RESOLUTION IS LOCAL-ONLY and one level deep by design. The generator
 * emits `#/components/...` references and nothing else — no remote refs, no
 * cycles — so a full JSON-Schema resolver would be several hundred lines
 * defending against inputs this document cannot contain.
 */

// VITE_API_BASE is ONE installation's gate: the reference and console show that installation only.
export function installationApiBase() {
  return `${(import.meta.env.VITE_API_BASE || "/api").replace(/\/+$/, "")}/ext/v1`;
}

export function openApiUrl() {
  return `${installationApiBase()}/openapi.json`;
}

export async function fetchDocument() {
  const res = await fetch(openApiUrl());
  if (!res.ok) {
    throw new Error(`The API contract could not be loaded (HTTP ${res.status}).`);
  }
  return res.json();
}

/** Follow a local `$ref`, or return the node unchanged. */
export function deref(doc, node, depth = 0) {
  if (!node || typeof node !== "object" || depth > 8) return node;
  if (!node.$ref) return node;
  if (!node.$ref.startsWith("#/")) return node;

  const target = node.$ref
    .slice(2)
    .split("/")
    .reduce((acc, key) => (acc ? acc[decodeURIComponent(key.replace(/~1/g, "/").replace(/~0/g, "~"))] : undefined), doc);

  return target ? deref(doc, target, depth + 1) : node;
}

/**
 * Every operation, flattened, in the order a reader wants them: grouped by tag,
 * then by path, then with GET before the verbs that change things.
 */
export function operations(doc) {
  const METHODS = ["get", "post", "put", "patch", "delete"];
  const out = [];

  for (const [path, item] of Object.entries(doc.paths || {})) {
    for (const method of METHODS) {
      const op = item[method];
      if (!op) continue;
      out.push({
        id: op.operationId || `${method}_${path}`,
        method: method.toUpperCase(),
        path,
        tag: (op.tags || ["other"])[0],
        summary: op.summary || "",
        description: op.description || "",
        deprecated: !!op.deprecated,
        scope: scopeOf(op),
        parameters: (op.parameters || []).map((p) => deref(doc, p)),
        requestBody: op.requestBody ? deref(doc, op.requestBody) : null,
        responses: op.responses || {},
        raw: op
      });
    }
  }

  return out.sort(
    (a, b) =>
      a.tag.localeCompare(b.tag) ||
      a.path.localeCompare(b.path) ||
      METHODS.indexOf(a.method.toLowerCase()) - METHODS.indexOf(b.method.toLowerCase())
  );
}

/**
 * The scope an operation needs.
 *
 * Read from the `security` block rather than from an `x-` extension, because
 * `security` is the part a generated client and a standards-aware tool actually
 * honour — an extension that disagreed with it would be a document that lints
 * clean and misleads.
 */
export function scopeOf(op) {
  const requirement = (op.security || [])[0];
  if (!requirement) return null;
  const scopes = Object.values(requirement)[0];
  return Array.isArray(scopes) && scopes.length ? scopes[0] : null;
}

export function tagsOf(doc, ops) {
  const described = new Map((doc.tags || []).map((t) => [t.name, t.description || ""]));
  const seen = [];
  for (const op of ops) {
    if (!seen.some((t) => t.name === op.tag)) {
      seen.push({ name: op.tag, description: described.get(op.tag) || "" });
    }
  }
  return seen;
}

/**
 * A worked example body for one operation, built from its schema.
 *
 * The console pre-fills with this so a developer's first request is one they can
 * send rather than one they have to compose. Examples in the document win;
 * otherwise a value is invented per type, and an enum's first member is used
 * because a made-up string would fail validation immediately.
 */
export function exampleFor(doc, schema, depth = 0) {
  const node = deref(doc, schema);
  if (!node || depth > 6) return null;
  if (node.example !== undefined) return node.example;
  if (node.default !== undefined) return node.default;
  if (Array.isArray(node.enum) && node.enum.length) return node.enum[0];

  switch (node.type) {
    case "object": {
      const out = {};
      for (const [key, value] of Object.entries(node.properties || {})) {
        out[key] = exampleFor(doc, value, depth + 1);
      }
      return out;
    }
    case "array":
      return [exampleFor(doc, node.items, depth + 1)].filter((v) => v !== null);
    case "integer":
      return node.minimum !== undefined ? node.minimum : 1;
    case "number":
      return 0;
    case "boolean":
      return false;
    case "string":
      if (node.format === "date-time") return new Date().toISOString();
      if (node.format === "uuid") return "00000000-0000-0000-0000-000000000000";
      return "";
    default:
      return null;
  }
}

/**
 * A Postman v2.1 collection.
 *
 * WHY GENERATED IN THE BROWSER rather than served as a file: the collection has
 * to carry the developer's own base URL and their own token variable, and a
 * static file on the server knows neither. It is also one fewer endpoint to keep
 * in step with the catalog.
 *
 * The access token is a COLLECTION VARIABLE, never a literal. A collection with
 * a live token baked into it is a credential that gets committed to a
 * repository, and this one is downloaded by every developer who reads the docs.
 */
export function toPostmanCollection(doc, { baseUrl } = {}) {
  const ops = operations(doc);
  const server = baseUrl || (doc.servers && doc.servers[0] && doc.servers[0].url) || "";
  const byTag = new Map();

  for (const op of ops) {
    if (!byTag.has(op.tag)) byTag.set(op.tag, []);

    const query = op.parameters
      .filter((p) => p.in === "query")
      .map((p) => ({
        key: p.name,
        value: p.schema && p.schema.default !== undefined ? String(p.schema.default) : "",
        description: p.description || "",
        // Optional parameters ship disabled so the first send is the simple
        // call, not every filter at once with empty values.
        disabled: !p.required
      }));

    // Path parameters become Postman `:name` segments AND variables, which is
    // what makes them editable in its own UI rather than buried in the URL.
    const pathParams = op.parameters.filter((p) => p.in === "path");

    byTag.get(op.tag).push({
      name: `${op.method} ${op.path}${op.summary ? ` — ${op.summary}` : ""}`,
      request: {
        method: op.method,
        header: [
          { key: "Authorization", value: "Bearer {{access_token}}" },
          { key: "Content-Type", value: "application/json" },
          ...(["POST", "PUT", "PATCH", "DELETE"].includes(op.method)
            ? [
                {
                  key: "Idempotency-Key",
                  value: "{{$guid}}",
                  description:
                    "Required on mutating endpoints. {{$guid}} makes each send a new key."
                }
              ]
            : [])
        ],
        url: {
          raw: `{{base_url}}${op.path}${query.length ? "?" : ""}`,
          host: ["{{base_url}}"],
          path: op.path.split("/").filter(Boolean),
          query,
          variable: pathParams.map((p) => ({
            key: p.name,
            value: "",
            description: p.description || ""
          }))
        },
        body:
          op.requestBody && ["POST", "PUT", "PATCH"].includes(op.method)
            ? {
                mode: "raw",
                raw: JSON.stringify(
                  exampleFor(
                    doc,
                    op.requestBody.content &&
                      op.requestBody.content["application/json"] &&
                      op.requestBody.content["application/json"].schema
                  ) || {},
                  null,
                  2
                ),
                options: { raw: { language: "json" } }
              }
            : undefined,
        description: op.description || op.summary
      }
    });
  }

  return {
    info: {
      name: `${(doc.info && doc.info.title) || "Zarokaa"} ${(doc.info && doc.info.version) || "v1"}`,
      description:
        (doc.info && doc.info.description) ||
        "Generated from the Zarokaa public API contract.",
      schema: "https://schema.getpostman.com/json/collection/v2.1.0/collection.json"
    },
    item: Array.from(byTag.entries()).map(([tag, items]) => ({ name: tag, item: items })),
    variable: [
      { key: "base_url", value: server, type: "string" },
      {
        key: "access_token",
        value: "",
        type: "string",
        description:
          "Paste an access token from the OAuth flow. Never commit a collection with this filled in."
      }
    ]
  };
}

/** Trigger a download of an object as JSON, without a round trip. */
export function downloadJson(filename, value) {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Released on the next tick rather than immediately: revoking synchronously
  // races the click in some browsers and produces an empty file.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
